import React, { useState } from 'react';

import Button from '@atlaskit/button';

import { PremiumFeatureGuard } from '@qality/common-components/src/atoms/PremiumFeatureGuard';
import { FEATURE_FLAG } from '@qality/common-config';
import { Assignee, ExecutionStatus } from '@qality/common-model';

import UserPicker from '../../../picker/UserPicker/UserPicker';
import { ExecutionStatusSelect } from '../../../select/execution-status-select/ExecutionStatusSelect';
import {
  handleBulkUpdateExecutionAssignee,
  handleBulkUpdateExecutionStatus,
} from '../../TestCycleTestCasesListTab.util';
import { useTestCasesInCycleList } from '../TestCaseInCycleListProvider';
import {
  AssigneeDropdownSlotStyled,
  BugsColumnVisibility,
  BugsSlotStyled,
  ContainerStyled,
  ItemsSlotStyled,
  StatusSelectSlotStyled,
  UncheckButtonSlotStyled,
} from './TestCycleDetailsPageBulkActionsBar.styled';

type Props = {
  canEdit: boolean;
  projectKey: string;
  hasAnyLinkedIssues: boolean;
};

export default function TestCycleDetailsPageBulkActionsBar({
  canEdit,
  projectKey,
  hasAnyLinkedIssues,
}: Props): JSX.Element {
  const {
    testCycle,
    testCycleActions,
    testCaseActions,
    selectedTestCasesIds,
    executionStatuses,
  } = useTestCasesInCycleList();

  const [isLoadingStatuses, setIsLoadingStatsues] = useState(false);
  const [isLoadingAssignees, setIsLoadingAssignees] = useState(false);

  const selectedTestCases = testCycle.testCases.filter((testCase) => {
    return selectedTestCasesIds.includes(testCase.id);
  });

  const getExecutionStatusSelectValue = () => {
    const selectedTestCasesStatuses = selectedTestCases.map(
      (tc) => tc.statusDetail
    );

    if (selectedTestCasesStatuses.length === 0) {
      return executionStatuses.defaultStatus;
    }

    const firstSelectedTestCaseStatus = selectedTestCasesStatuses[0];
    const areAllSelectedStatusesTheSame = selectedTestCasesStatuses.every(
      (s) => s.id === firstSelectedTestCaseStatus.id
    );

    return areAllSelectedStatusesTheSame
      ? firstSelectedTestCaseStatus
      : executionStatuses.defaultStatus;
  };

  const handleUncheckButtonClick = () => {
    testCaseActions.selectTestCases([]);
  };

  const handleExecutionAssigneeBulkChange = async (
    newExecutionAssignee: Assignee
  ) => {
    setIsLoadingAssignees(true);
    await handleBulkUpdateExecutionAssignee(
      selectedTestCases,
      newExecutionAssignee,
      testCycleActions.updateTestCycle,
      testCycle
    );
    setIsLoadingAssignees(false);
  };

  const handleExecutionStatusBulkChange = async (
    newExecutionStatus: ExecutionStatus
  ) => {
    setIsLoadingStatsues(true);
    await handleBulkUpdateExecutionStatus(
      selectedTestCases,
      newExecutionStatus,
      testCycleActions.updateTestCycle,
      testCycle
    );
    setIsLoadingStatsues(false);
  };

  const getBugsColumn = (): BugsColumnVisibility => {
    if (!FEATURE_FLAG.isPremium) {
      return 'hidden';
    }

    if (hasAnyLinkedIssues) {
      return 'visible-content';
    }

    return 'visible-empty';
  };

  const isBarDisplayed = selectedTestCasesIds.length > 0 && canEdit;

  if (!isBarDisplayed) {
    return null;
  }

  return (
    <ContainerStyled
      data-testid='test-cycle-navigation'
      bugsColumnVisibility={getBugsColumn()}
    >
      <ItemsSlotStyled>
        {selectedTestCasesIds.length}{' '}
        {selectedTestCasesIds.length === 1 ? 'item' : 'items'}
      </ItemsSlotStyled>
      <StatusSelectSlotStyled label='Status'>
        <ExecutionStatusSelect
          value={getExecutionStatusSelectValue()}
          statuses={executionStatuses.allStatuses}
          onChange={handleExecutionStatusBulkChange}
          isLoading={isLoadingStatuses}
        />
      </StatusSelectSlotStyled>
      <AssigneeDropdownSlotStyled label='Assignee'>
        <UserPicker
          onChange={handleExecutionAssigneeBulkChange}
          projectKey={projectKey}
          isDisabled={!canEdit}
          menuPlacement='top'
          isLoadingAssignees={isLoadingAssignees}
        />
      </AssigneeDropdownSlotStyled>
      <PremiumFeatureGuard premium={<BugsSlotStyled />} />
      <UncheckButtonSlotStyled>
        <Button onClick={handleUncheckButtonClick}>Uncheck</Button>
      </UncheckButtonSlotStyled>
    </ContainerStyled>
  );
}
